import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { trpc } from "../lib/trpc";
import { CompanyFormModal } from "../components/parties/CompanyFormModal";
import { Icon } from "../components/ui/Icon";
import { useIsMobile } from "../lib/useIsMobile";
import { MCard } from "../components/mobile/primitives";
import "./Items.css";

interface CompanyRow {
  id: string;
  code: string;
  name: string;
  taxNumber: string | null;
  email: string | null;
  phone: string | null;
  city: string | null;
  country: string | null;
  isActive: boolean;
  isSupplier: boolean;
  isCustomer: boolean;
  isCarrier: boolean;
  isDriver: boolean;
  supplierCode: string | null;
  customerCode: string | null;
}

type CompanyRoleFilter = "" | "supplier" | "customer" | "carrier" | "driver";

export function CompaniesPage() {
  const isMobile = useIsMobile();
  const [q, setQ] = useState("");
  const [role, setRole] = useState<CompanyRoleFilter>("");
  const [page, setPage] = useState(1);
  const pageSize = 20;
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const input = useMemo(
    () => ({ page, pageSize, q: q.trim() || undefined, role: role || undefined }),
    [page, q, role],
  );
  const listQ = trpc.company.list.useQuery(input);

  const rows = (listQ.data?.rows ?? []) as CompanyRow[];
  const total = listQ.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  function openCreate() { setEditingId(null); setModalOpen(true); }
  function openEdit(id: string) { setEditingId(id); setModalOpen(true); }

  if (isMobile) {
    return (
      <>
        <div className="m-stickytop">
          <button type="button" className="btn-md primary" onClick={openCreate}><Icon name="plus" size={15} />Yeni Cari</button>
          <div className="m-search">
            <Icon name="search" size={15} />
            <input type="search" placeholder="Kod, ad veya vergi no ara…" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} aria-label="Ara" />
          </div>
          <div className="m-chips">
            {([["", "Tümü"], ["supplier", "Tedarikçi"], ["customer", "Müşteri"], ["carrier", "Nakliyeci"], ["driver", "Sürücü"]] as Array<[CompanyRoleFilter, string]>).map(([v, lb]) => (
              <button key={lb} type="button" className={`m-chip${role === v ? " act" : ""}`} onClick={() => { setRole(v); setPage(1); }}>{lb}</button>
            ))}
          </div>
        </div>

        {listQ.isLoading ? (
          <div className="m-empty">Yükleniyor…</div>
        ) : listQ.isError ? (
          <div className="m-empty" style={{ color: "var(--danger)" }}>{listQ.error?.message}</div>
        ) : rows.length === 0 ? (
          <div className="m-empty">{q || role ? "Filtreye uyan sonuç yok." : "Henüz cari kaydı yok"}</div>
        ) : (
          <>
            {rows.map((r) => (
              <MCard pad key={r.id} onClick={() => openEdit(r.id)} style={{ cursor: "pointer" }}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: "block", fontSize: 13.5, fontWeight: 700, color: "var(--neutral-900)" }}>{r.name}</span>
                    <span className="mono" style={{ display: "block", fontSize: 11, color: "var(--neutral-400)" }}>{r.code}</span>
                  </span>
                  {r.isActive ? <span className="badge b-success no-dot">Aktif</span> : <span className="badge b-neutral no-dot">Pasif</span>}
                </div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }}>
                  {r.isSupplier ? <RolePill kind="supplier" code={r.supplierCode} /> : null}
                  {r.isCustomer ? <RolePill kind="customer" code={r.customerCode} /> : null}
                  {r.isCarrier ? <RolePill kind="carrier" code={null} /> : null}
                  {r.isDriver ? <RolePill kind="driver" code={null} /> : null}
                  {!r.isSupplier && !r.isCustomer && !r.isCarrier && !r.isDriver ? <span style={{ color: "var(--neutral-400)", fontSize: 12 }}>Rol atanmadı</span> : null}
                </div>
                {(r.taxNumber || r.city) ? (
                  <div style={{ display: "flex", gap: 16, marginTop: 8, fontSize: 11.5, color: "var(--neutral-600)" }}>
                    {r.taxNumber ? <span><span style={{ color: "var(--neutral-400)" }}>VKN </span><span className="mono">{r.taxNumber}</span></span> : null}
                    {r.city ? <span>{r.city}{r.country ? ` · ${r.country}` : ""}</span> : null}
                  </div>
                ) : null}
                {/* HESAP EKSTRESİ — masaüstünde satır butonu ve kod bağlantısı var; mobil kartta
                    hiç yoktu, yani telefondan cari hesabına ULAŞILAMIYORDU. Kartın kendisi
                    "düzenle"ye gittiği için bağlantı ayrı bir eylem satırı olarak durur ve
                    tıklaması karta SIÇRAMAZ (stopPropagation). Dokunma hedefi 44px. */}
                <Link
                  to={`/companies/${r.id}/hesap`}
                  onClick={(e) => e.stopPropagation()}
                  className="cmp-m-acc"
                  aria-label={`${r.name} hesap ekstresi`}
                >
                  <Icon name="banknote" size={15} />
                  Cari Hesap Ekstresi
                  <Icon name="chevRight" size={14} />
                </Link>
              </MCard>
            ))}
            {totalPages > 1 ? (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 12, marginTop: 2 }}>
                <button type="button" className="btn-md secondary sm" disabled={page === 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>‹ Önceki</button>
                <span className="mono" style={{ fontSize: 12.5, color: "var(--neutral-500)" }}>{page} / {totalPages}</span>
                <button type="button" className="btn-md secondary sm" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>Sonraki ›</button>
              </div>
            ) : null}
          </>
        )}
        <CompanyFormModal open={modalOpen} companyId={editingId} onClose={() => setModalOpen(false)} />
      </>
    );
  }

  return (
    <div className="items-page">
      <div className="items-crumb">
        <Link to="/dashboard" style={{ color: "var(--neutral-500)", textDecoration: "none" }}>Panel</Link>
        <span className="sep">/</span>
        <span className="cur">Cariler</span>
      </div>

      <div className="items-head">
        <div>
          <h1 className="title">Cariler</h1>
          <p className="sub">Tüm iş ortakları — tedarikçi, müşteri, nakliyeci ve/veya sürücü tek kartta</p>
        </div>
        <button type="button" className="btn-md primary" onClick={openCreate}>
          <Icon name="plus" size={14} />Yeni Cari
        </button>
      </div>

      <div className="dt2-wrap">
        <div className="dt2-toolbar">
          <div className="dt2-search-box">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="11" cy="11" r="7" />
              <path d="m21 21-4.3-4.3" />
            </svg>
            <input
              type="search"
              placeholder="Kod, ad veya vergi no ara…"
              value={q}
              onChange={(e) => { setQ(e.target.value); setPage(1); }}
              aria-label="Ara"
            />
          </div>
          <select
            className="form-select"
            style={{ height: 32, width: "auto", minWidth: 140 }}
            value={role}
            onChange={(e) => { setRole(e.target.value as CompanyRoleFilter); setPage(1); }}
            aria-label="Rol filtresi"
          >
            <option value="">Tüm roller</option>
            <option value="supplier">Tedarikçiler</option>
            <option value="customer">Müşteriler</option>
            <option value="carrier">Nakliyeciler</option>
            <option value="driver">Sürücüler</option>
          </select>
          <span className="grow" />
          <button type="button" className="btn" onClick={() => listQ.refetch()} disabled={listQ.isFetching}>
            {listQ.isFetching ? "Yükleniyor…" : "Yenile"}
          </button>
        </div>

        <div className="dt2-scroll">
          {listQ.isLoading ? (
            <div className="empty-state"><p className="ttl">Yükleniyor…</p></div>
          ) : listQ.isError ? (
            <div className="empty-state">
              <p className="ttl" style={{ color: "var(--danger)" }}>Veri yüklenemedi</p>
              <p className="sub">{listQ.error?.message}</p>
            </div>
          ) : rows.length === 0 ? (
            <div className="empty-state">
              <p className="ttl">Henüz cari kaydı yok</p>
              <p className="sub">{q || role ? "Filtreye uyan sonuç yok." : "İlk cariyi ekleyin."}</p>
              {!q && !role ? (
                <button type="button" className="btn-md primary" style={{ marginTop: 12 }} onClick={openCreate}>
                  <Icon name="plus" size={14} />Yeni Cari
                </button>
              ) : null}
            </div>
          ) : (
            <table className="dt2">
              <thead>
                <tr>
                  <th style={{ width: 140 }}>Kod</th>
                  <th>Firma Adı</th>
                  <th style={{ width: 200 }}>Roller</th>
                  <th style={{ width: 130 }}>Vergi No</th>
                  <th style={{ width: 140 }}>Şehir / Ülke</th>
                  <th style={{ width: 90 }}>Durum</th>
                  <th style={{ width: 90 }}>Eylemler</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td>
                      {/* Kod TIKLANABİLİR: cari hesap ekstresini SEKME olarak açar.
                          Satırın tamamını tıklanabilir yapmak düzenleme butonuyla çakışırdı. */}
                      <Link to={`/companies/${r.id}/hesap`} className="cell-id cmp-acc-link" title={`${r.name} hesap ekstresi`}>{r.code}</Link>
                    </td>
                    <td>{r.name}</td>
                    <td>
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                        {r.isSupplier ? <RolePill kind="supplier" code={r.supplierCode} /> : null}
                        {r.isCustomer ? <RolePill kind="customer" code={r.customerCode} /> : null}
                        {r.isCarrier ? <RolePill kind="carrier" code={null} /> : null}
                        {r.isDriver ? <RolePill kind="driver" code={null} /> : null}
                        {!r.isSupplier && !r.isCustomer && !r.isCarrier && !r.isDriver ? <span style={{ color: "var(--neutral-400)" }}>—</span> : null}
                      </div>
                    </td>
                    <td>{r.taxNumber ? <span className="mono" style={{ fontSize: 12 }}>{r.taxNumber}</span> : <span style={{ color: "var(--neutral-400)" }}>—</span>}</td>
                    <td>{r.city ? `${r.city}${r.country ? " · " + r.country : ""}` : <span style={{ color: "var(--neutral-400)" }}>—</span>}</td>
                    <td>{r.isActive ? <span className="badge-pill b-success">Aktif</span> : <span className="badge-pill">Pasif</span>}</td>
                    <td>
                      <div className="row-acts">
                        <Link to={`/companies/${r.id}/hesap`} className="iact info" aria-label={`${r.name} hesap ekstresi`} title="Cari hesap ekstresi — borç/alacak, tahsilat, ticaret hacmi">
                          <Icon name="banknote" size={15} />
                        </Link>
                        <button type="button" className="iact" aria-label="Düzenle" title="Düzenle" onClick={() => openEdit(r.id)}>
                          <Icon name="edit" size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="dt2-foot">
          <div>
            <b>{total}</b> kayıttan <b>{rows.length === 0 ? 0 : (page - 1) * pageSize + 1}–{(page - 1) * pageSize + rows.length}</b> arası
          </div>
          <div className="pager">
            <button type="button" className="pg" aria-label="Önceki sayfa" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>‹</button>
            <span className="pg active">{page}</span>
            <span style={{ color: "var(--neutral-400)", padding: "0 8px", fontSize: 12 }}>/ {totalPages}</span>
            <button type="button" className="pg" aria-label="Sonraki sayfa" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages}>›</button>
          </div>
        </div>
      </div>

      <CompanyFormModal
        open={modalOpen}
        companyId={editingId}
        onClose={() => setModalOpen(false)}
      />
    </div>
  );
}

function RolePill({ kind, code }: { kind: "supplier" | "customer" | "carrier" | "driver"; code: string | null }) {
  const style: Record<typeof kind, { bg: string; fg: string; border: string; icon: string; label: string }> = {
    supplier: { bg: "var(--info-subtle, #E0EBFF)", fg: "var(--info-text, #1D4ED8)", border: "var(--info-border, #BFD4FE)", icon: "truck", label: "Tedarikçi" },
    customer: { bg: "var(--primary-subtle, #EAEEF5)", fg: "var(--primary)", border: "var(--neutral-200)", icon: "users", label: "Müşteri" },
    carrier: { bg: "var(--success-subtle, #E7F6EC)", fg: "var(--success-text, #15803D)", border: "var(--neutral-200)", icon: "truck", label: "Nakliyeci" },
    driver: { bg: "var(--warning-subtle, #FEF3E2)", fg: "var(--warning-text, #B45309)", border: "var(--neutral-200)", icon: "user", label: "Sürücü" },
  };
  const s = style[kind];
  return (
    <span
      title={code ?? undefined}
      style={{
        display: "inline-flex", alignItems: "center", gap: 5,
        padding: "2px 9px", borderRadius: 9999, fontSize: 11.5, fontWeight: 600,
        background: s.bg, color: s.fg, border: `1px solid ${s.border}`,
      }}
    >
      <Icon name={s.icon} size={12} />
      {s.label}
    </span>
  );
}
